import React, { useEffect, useState } from 'react';
import { Ticket, AuditEvent } from '../../../entities/ticket';
import { GetTicketAuditHistoryUseCase } from '../application';
import { ExternalDataTabs } from './ExternalDataTabs';
import { HudleStaffActions } from '../../hudle-staff/HudleStaffActions';
import * as S from './TicketDetails.styles';

interface TicketDetailsProps {
  ticket: Ticket | null;
}

export const TicketDetails: React.FC<TicketDetailsProps> = ({ ticket }) => {
  const [auditHistory, setAuditHistory] = useState<AuditEvent[]>([]);

  const getTicketAuditHistoryUseCase = new GetTicketAuditHistoryUseCase();

  useEffect(() => {
    if (ticket) {
      loadAuditHistory();
    } else {
      setAuditHistory([]);
    }
  }, [ticket?.id]);

  const loadAuditHistory = async () => {
    if (!ticket) return;
    
    try {
      const data = await getTicketAuditHistoryUseCase.execute(ticket.id);
      setAuditHistory(data);
    } catch (error) {
      console.error('Ошибка загрузки истории аудита:', error);
    }
  };

  const getClientInitial = (name: string): string => {
    return name.charAt(0).toUpperCase();
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!ticket) {
    return (
      <S.TicketDetailsContainer>
        <S.EmptyStateContainer>
          Выберите тикет для просмотра деталей
        </S.EmptyStateContainer>
      </S.TicketDetailsContainer>
    );
  }

  const isActive = ticket.status === 'new' || ticket.status === 'active' || ticket.status === 'waiting_info' || ticket.status === 'in_programming';

  return (
    <S.TicketDetailsContainer>
      <S.Section>
        <S.ClientHeader>
          <S.ClientIcon>
            {getClientInitial(ticket.client.name)}
          </S.ClientIcon>
          <S.ClientInfo>
            <S.ClientName>{ticket.client.name}</S.ClientName>
            {ticket.client.type === 'enterprise' && (
              <S.ClientTag>ENTERPRISE</S.ClientTag>
            )}
          </S.ClientInfo>
        </S.ClientHeader>
        {ticket.client.uid && (
          <S.UID>UID: {ticket.client.uid}</S.UID>
        )}
      </S.Section>

      <S.Section>
        <S.SectionTitle>ОСНОВНОЙ КОНТАКТ</S.SectionTitle>
        <S.SectionContent>
          {ticket.client.mainContact?.email || '—'}
        </S.SectionContent>
      </S.Section>

      {ticket.client.region && (
        <S.Section>
          <S.SectionTitle>РЕГИОН</S.SectionTitle>
          <S.SectionContent>
            {ticket.client.region}
          </S.SectionContent>
        </S.Section>
      )}

      <S.Section>
        <S.SectionTitle>САЙТ</S.SectionTitle>
        <S.SectionContent>
          <S.SiteBadge $site={ticket.site}>
            {ticket.site === 'hudle' ? 'Hudle' : ticket.site === 'unknown' ? 'Неизвестный' : 'Турнирный сайт'}
          </S.SiteBadge>
        </S.SectionContent>
      </S.Section>

      <S.Section>
        <S.SectionTitle>ТЕКУЩИЙ СТАТУС</S.SectionTitle>
        <S.StatusIndicator $active={isActive}>
          <S.StatusDot $active={isActive} />
          <S.SectionContent>
            {ticket.status === 'new' && 'Новый'}
            {ticket.status === 'active' && 'Открыто'}
            {ticket.status === 'waiting_info' && 'Ожидаем ответ клиента'}
            {ticket.status === 'in_programming' && 'В программирование'}
            {ticket.status === 'resolved' && 'Решено'}
            {ticket.status === 'closed' && 'Закрыто'}
            {ticket.status === 'spam' && 'Спам'}
            {ticket.status === 'duplicate' && 'Дубликат'}
          </S.SectionContent>
        </S.StatusIndicator>
      </S.Section>

      <S.AdminToolsSection>
        <S.AdminSectionTitle>ИНСТРУМЕНТЫ</S.AdminSectionTitle>
        <S.CompactToolsGrid>
          <S.CompactToolButton onClick={() => window.open('https://wiki.lumos-team.ru', '_blank')}>
            <S.CompactToolLabel>Документация</S.CompactToolLabel>
          </S.CompactToolButton>
          <S.CompactToolButton onClick={() => window.open('https://admin.hudle.ru', '_blank')}>
            <S.CompactToolLabel>Админка Hudle</S.CompactToolLabel>
          </S.CompactToolButton>
          <S.CompactToolButton onClick={() => window.open('https://tournaments.hudle.ru/admin/', '_blank')}>
            <S.CompactToolLabel>Админка Турнирки</S.CompactToolLabel>
          </S.CompactToolButton>
          <S.CompactToolButton onClick={() => window.open('https://kibana.lumos-team.ru/', '_blank')}>
            <S.CompactToolLabel>Логи</S.CompactToolLabel>
          </S.CompactToolButton>
          <S.CompactToolButton onClick={() => window.open('https://youtrack.lumos-team.ru/', '_blank')}>
            <S.CompactToolLabel>Трекер</S.CompactToolLabel>
          </S.CompactToolButton>
          <HudleStaffActions
            variant="compact"
            clientId={ticket.client.id}
            uid={ticket.client.uid}
            externalSite={ticket.client.externalSite || ticket.site}
            email={ticket.client.mainContact?.email}
            isBanned={Boolean(ticket.client.externalData?.user?.is_banned)}
            ticketId={ticket.id}
          />
        </S.CompactToolsGrid>
      </S.AdminToolsSection>

      {ticket.client.externalData && Object.keys(ticket.client.externalData).length > 0 && (
        <S.Section>
          <S.SectionTitle>ДОПОЛНИТЕЛЬНАЯ ИНФОРМАЦИЯ</S.SectionTitle>
          <ExternalDataTabs ticket={ticket} />
        </S.Section>
      )}

      <S.AuditHistoryContainer>
        <S.SectionTitle>ИСТОРИЯ АУДИТА ДИАЛОГА</S.SectionTitle>
        {auditHistory.length === 0 ? (
          <S.EmptySectionContent>
            История пуста
          </S.EmptySectionContent>
        ) : (
          auditHistory.map(event => (
            <S.AuditEvent key={event.id}>
              <S.AuditEventDescription>{event.description}</S.AuditEventDescription>
              <S.AuditEventMeta>
                {event.userName && `${event.userName} • `}
                {formatTimestamp(event.timestamp)}
              </S.AuditEventMeta>
            </S.AuditEvent>
          ))
        )}
      </S.AuditHistoryContainer>
    </S.TicketDetailsContainer>
  );
};

